/**
 * Shared helpers for the native (dependency-free) horizontal carousels.
 * The carousels are plain scroll containers using CSS scroll-snap, so navigation,
 * begin/end state and drag-to-scroll are all derived from scroll geometry.
 */

// Tolerance in px to absorb sub-pixel scroll positions and zoom rounding.
const SCROLL_EDGE_TOLERANCE = 2;

// A pointer has to travel this far before it counts as a drag rather than a click.
// Mirrors the `threshold: 5` the carousels inherited from their previous slider.
const DRAG_THRESHOLD = 5;

// Elements that own their own pointer interaction; a drag must not start on them.
const INTERACTIVE_ELEMENTS = 'input, select, option, textarea, button, video, label, a, [contenteditable]';

const clamp = (value, max) => Math.max(0, Math.min(value, max));

const isCenterAligned = (slide) =>
  window.getComputedStyle(slide).scrollSnapAlign.includes('center');

/**
 * Scroll offset at which `slide` rests, honouring its scroll-snap alignment.
 * `offsetLeft` differences are layout coordinates, so they already include the
 * track's scroll padding used to centre the first and last slide.
 * @param {HTMLElement} track
 * @param {HTMLElement} slide
 * @returns {number}
 */
const getSlideScrollLeft = (track, slide) => {
  const left = slide.offsetLeft - track.offsetLeft;
  if (!isCenterAligned(slide)) return left;
  return left - (track.clientWidth - slide.offsetWidth) / 2;
};

/**
 * Furthest useful scroll offset: the last slide centred when slides are centre
 * aligned, otherwise flush with the end of the track. Derived from the slide grid
 * rather than from `scrollWidth` so that content painted outside a slide (the
 * builder's delete button sits at `right: -5%`) cannot extend the range.
 * @param {HTMLElement} track
 * @returns {number}
 */
const getMaxScrollLeft = (track) => {
  const scrollableWidth = track.scrollWidth - track.clientWidth;
  const last = track.children[track.children.length - 1];
  if (!last) return Math.max(0, scrollableWidth);
  const gridEnd = isCenterAligned(last)
    ? getSlideScrollLeft(track, last)
    : last.offsetLeft - track.offsetLeft + last.offsetWidth - track.clientWidth;
  return clamp(gridEnd, Math.max(0, scrollableWidth));
};

/** Resting scroll offset of every slide, clamped to the scrollable range. */
const getSnapPositions = (track) => {
  const max = getMaxScrollLeft(track);
  return [...track.children].map((slide) => clamp(getSlideScrollLeft(track, slide), max));
};

const getNearestSnapIndex = (positions, scrollLeft) =>
  positions.reduce(
    (nearest, position, index) =>
      Math.abs(position - scrollLeft) < Math.abs(positions[nearest] - scrollLeft) ? index : nearest,
    0
  );

/**
 * Read the prev/next availability of a horizontal scroll container.
 * @param {HTMLElement|null|undefined} track
 * @returns {{ isEnablePrevButton: boolean, isEnableNextButton: boolean }}
 */
export const getScrollNavigationState = (track) => {
  if (!track) return { isEnablePrevButton: false, isEnableNextButton: false };
  const maxScrollLeft = getMaxScrollLeft(track);
  if (maxScrollLeft <= SCROLL_EDGE_TOLERANCE) {
    return { isEnablePrevButton: false, isEnableNextButton: false };
  }
  return {
    isEnablePrevButton: track.scrollLeft > SCROLL_EDGE_TOLERANCE,
    isEnableNextButton: track.scrollLeft < maxScrollLeft - SCROLL_EDGE_TOLERANCE,
  };
};

/**
 * Scroll a track to the neighbouring slide in the given direction.
 * @param {HTMLElement|null|undefined} track
 * @param {number} direction 1 for next, -1 for previous
 */
export const scrollTrackByOneSlide = (track, direction) => {
  if (!track) return;
  const positions = getSnapPositions(track);
  if (!positions.length) return;
  const current = getNearestSnapIndex(positions, track.scrollLeft);
  const target = Math.min(positions.length - 1, Math.max(0, current + direction));
  track.scrollTo({ left: positions[target], behavior: 'smooth' });
};

/**
 * Scroll a track so the slide at `index` comes to rest in view.
 * @param {HTMLElement|null|undefined} track
 * @param {number} index
 */
export const scrollTrackToSlide = (track, index) => {
  if (!track) return;
  const slide = track.children[index];
  if (!slide) return;
  track.scrollTo({ left: clamp(getSlideScrollLeft(track, slide), getMaxScrollLeft(track)), behavior: 'smooth' });
};

/**
 * Enable mouse drag-to-scroll on a scroll-snap track, the way a pointer drag used
 * to move the slider. Touch and pen keep scrolling natively.
 *
 * Snapping is suspended for the duration of the drag (a mandatory snap would fight
 * every `scrollLeft` write) and the track is snapped to the nearest slide on release.
 * While dragging, `data-dragging` is set on the track so styles can swap the cursor.
 *
 * @param {HTMLElement|null|undefined} track
 * @returns {Function} teardown that removes every listener it added
 */
const dragScrollTeardowns = new WeakMap();

export const enableDragScroll = (track) => {
  if (!track) return () => {};
  // Safe to call on every update: a track is only ever wired once.
  if (dragScrollTeardowns.has(track)) return dragScrollTeardowns.get(track);

  let pointerId = null;
  let startX = 0;
  let startScrollLeft = 0;
  let isDragging = false;

  const stopDrag = () => {
    if (pointerId === null) return;
    const wasDragging = isDragging;
    if (track.hasPointerCapture?.(pointerId)) track.releasePointerCapture(pointerId);
    pointerId = null;
    isDragging = false;
    track.removeAttribute('data-dragging');
    track.style.scrollSnapType = '';
    if (wasDragging) {
      const positions = getSnapPositions(track);
      if (positions.length) {
        track.scrollTo({ left: positions[getNearestSnapIndex(positions, track.scrollLeft)], behavior: 'smooth' });
      }
    }
    return wasDragging;
  };

  // A drag only starts from the slide strip itself, never from the empty gutter that
  // centred slides leave beside the cards.
  const isWithinSlideStrip = (event) => {
    const slides = [...track.children];
    if (!slides.length) return false;
    if (slides.some((slide) => slide.contains(event.target))) return true;
    const first = slides[0].getBoundingClientRect();
    const last = slides[slides.length - 1].getBoundingClientRect();
    return event.clientX >= first.left && event.clientX <= last.right;
  };

  const onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest?.(INTERACTIVE_ELEMENTS)) return;
    if (!isWithinSlideStrip(event)) return;
    // Clear any state left behind by a release this track never saw.
    stopDrag();
    pointerId = event.pointerId;
    startX = event.clientX;
    startScrollLeft = track.scrollLeft;
    isDragging = false;
    // Deliberately no setPointerCapture here: capturing on pointerdown
    // re-targets the compatibility mouse events — including the following
    // `click` — at the track, so a click on anything inside a card that is not
    // a native interactive element never reaches it. That silently broke the
    // variable-picker and emoji icons of the rich text editors rendered inside
    // carousel and rich card cards (they are custom elements, so the
    // INTERACTIVE_ELEMENTS bail-out above does not cover them). Capture is
    // taken in `onPointerMove` instead, once the pointer has actually travelled
    // far enough to be a drag rather than a click.
  };

  const onPointerMove = (event) => {
    if (event.pointerId !== pointerId) return;
    const delta = event.clientX - startX;
    if (!isDragging) {
      if (Math.abs(delta) < DRAG_THRESHOLD) return;
      isDragging = true;
      // From here on the gesture is a drag, so take the pointer: the track keeps
      // following the pointer even when it leaves the track's bounds.
      track.setPointerCapture?.(pointerId);
      track.setAttribute('data-dragging', '');
      // Suspend snapping so the track follows the pointer instead of jumping.
      track.style.scrollSnapType = 'none';
    }
    event.preventDefault();
    track.scrollLeft = startScrollLeft - delta;
  };

  const onPointerUp = (event) => {
    if (event.pointerId !== pointerId) return;
    if (stopDrag()) {
      // Swallow the click the drag would otherwise deliver to a card.
      track.addEventListener('click', swallowClick, { capture: true, once: true });
    }
  };

  const swallowClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const onLostCapture = () => stopDrag();

  track.addEventListener('pointerdown', onPointerDown);
  track.addEventListener('pointermove', onPointerMove);
  track.addEventListener('lostpointercapture', onLostCapture);
  // Release is watched on the window: until the drag threshold is crossed the
  // track holds no pointer capture, so the pointer can come up anywhere.
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onLostCapture);

  const teardown = () => {
    stopDrag();
    dragScrollTeardowns.delete(track);
    track.removeEventListener('pointerdown', onPointerDown);
    track.removeEventListener('pointermove', onPointerMove);
    track.removeEventListener('lostpointercapture', onLostCapture);
    track.removeEventListener('click', swallowClick, { capture: true });
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onLostCapture);
  };
  dragScrollTeardowns.set(track, teardown);
  return teardown;
};

/**
 * Wrap a handler so it runs at most once per animation frame.
 * Keeps scroll listeners cheap without debounce lag.
 * @param {Function} handler
 * @returns {Function & { cancel: Function }}
 */
export const rafThrottle = (handler) => {
  let frameId = null;
  const throttled = (...args) => {
    if (frameId !== null) return;
    frameId = window.requestAnimationFrame(() => {
      frameId = null;
      handler(...args);
    });
  };
  throttled.cancel = () => {
    if (frameId !== null) {
      window.cancelAnimationFrame(frameId);
      frameId = null;
    }
  };
  return throttled;
};
